import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
// 🔴 **Kein `X-Admin-Key` mehr** (04.11.2026): beide Endpunkte tragen die
// Kontenliste des Tores (`_konto_riegel()` in `backend/agent/sicht_api.py`), das
// Bearer-JWT setzt der axios-Interceptor der App. Dieselbe Begruendung wie in
// `useAgentZahlen`.

/**
 * useAgentBefunde: was der Agent gefunden hat, in Zahlen (N102, B3).
 *
 * @description
 *   Zwei Aufrufe in EINEM Zug: `GET /api/agent/fehlalarme` (gefeuert gegen
 *   weggeklickt, je Pruefung) und `GET /api/agent/befunde/bestand` (die offenen
 *   Befunde OHNE Fall). Beide speisen dieselbe Flaeche unter den Agenten-Zahlen.
 *
 *   🔴 **Ein gemeinsamer Fehlerzustand, und der Grund ist gemessen:** beide
 *   Endpunkte haengen an derselben Tuer (`_konto_riegel()`), derselben Tabelle
 *   (`rma_agent_befund`) und derselben Datenbank. Ein Fehlschlag des einen ist
 *   praktisch immer auch einer des anderen (Anmeldung oder Konto abgewiesen, DB weg).
 *   Zwei getrennte Fehlerflaechen haetten in genau diesen Faellen zweimal
 *   dasselbe gesagt und die Karte doppelt so hoch gemacht. Was das KOSTET, steht
 *   daneben: faellt einer der zwei Aufrufe aus, zeigt die Flaeche keinen der
 *   beiden. Das ist der bewusste Tausch, nicht ein Versehen.
 *
 *   🔴 **Die zwei Zahlen sind NICHT dasselbe.** `fehlalarme` zaehlt Befundzeilen
 *   ueber ALLE Faelle (mit und ohne Fall), `bestand` nur die ohne Fall. Wer die
 *   Bestandsliste als Teilmenge der Quote liest, liest sie falsch; die Antwort
 *   fuehrt deshalb `zaehlweise` mit, und die Anzeige sagt es dazu.
 *
 * @useWhen
 *   - In `AgentZahlen`, einmal je Seite; die Anzeige bekommt das Ergebnis
 *
 * @dontUseFor
 *   - Fuer die Befunde EINES Falls: das ist `useBefunde` in der Fallansicht
 *   - Als Verlauf: beide Endpunkte liefern den Stand, keine Zeitreihe
 *
 * @related
 *   - useAgentZahlen: dieselbe Tuer, dieselbe Bauform, eigener Haken
 *     (verschiedene Lebensdauer: die Zahlen des Tages aendern sich im Takt, die
 *     Befundlage nur, wenn ein Lauf schreibt)
 *   - AgentZahlen: die Anzeige; sie holt ihre Daten nicht selbst
 *
 * @returns {{fehlalarme, bestand, loading, error, reload}}
 */
export default function useAgentBefunde() {
  const [fehlalarme, setFehlalarme] = useState(null)
  const [bestand, setBestand] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/agent/fehlalarme`),
        axios.get(`${API}/api/agent/befunde/bestand`),
      ])
      setFehlalarme(a.data || null)
      setBestand(b.data || null)
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'agent_findings_failed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  return { fehlalarme, bestand, loading, error, reload: load }
}
